import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BlockIcon from "@mui/icons-material/Block";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  generatePath,
  Link as RouterLink,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ROUTES_NAMES } from "../components/routes";
import apiService, {
  type MediaChannelAccountDiscoveryResult,
  type MediaChannelAccountSearchResult,
  type MediaChannelDetail,
  type MediaChannelImageSyncResult,
  type MediaChannelMainImageSyncResult,
  type MediaChannelRow,
  type MediaChannelSyncedFields,
} from "../services/api";
import { getPodcastImageUrl, getS3Url } from "../utils/imageUtils";
import { SourceChips } from "./MediaChannelsExplorer";
import ChannelProgramsSection from "../components/ChannelProgramsSection";

type PlatformKey = MediaChannelImageSyncResult["platform"];

type ChannelEditValues = {
  name: string;
  slug: string;
  description: string;
  type: "tv" | "noticias" | "radio" | "digital";
  category: "politics" | "other";
  trackLivestream: boolean;
  youtubeChannelId: string;
  youtubeUsername: string;
  instagramUsername: string;
  facebookUsername: string;
  tiktokUsername: string;
  spotifyId: string;
};

const PLATFORM_LABELS: Record<PlatformKey, string> = {
  youtube: "YouTube",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  spotify: "Spotify",
};

function editValuesFromRow(row: MediaChannelRow): ChannelEditValues {
  return {
    name: row.name,
    slug: row.slug,
    description: row.description ?? "",
    type: row.type,
    category: row.category,
    trackLivestream: row.trackLivestream,
    youtubeChannelId: row.youtubeChannelId ?? "",
    youtubeUsername: row.youtubeUsername ?? "",
    instagramUsername: row.instagramUsername ?? "",
    facebookUsername: row.facebookUsername ?? "",
    tiktokUsername: row.tiktokUsername ?? "",
    spotifyId: row.spotifyId ?? "",
  };
}

function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: string; message?: string } } }).response
    ?.data;
  return data?.error || data?.message || fallback;
}

function platformUrl(platform: PlatformKey, values: ChannelEditValues): string | null {
  switch (platform) {
    case "youtube":
      if (values.youtubeUsername.trim()) {
        return `https://www.youtube.com/@${values.youtubeUsername.trim().replace(/^@/, "")}`;
      }
      return values.youtubeChannelId.trim()
        ? `https://www.youtube.com/channel/${values.youtubeChannelId.trim()}`
        : null;
    case "instagram":
      return values.instagramUsername.trim()
        ? `https://www.instagram.com/${values.instagramUsername.trim().replace(/^@/, "")}/`
        : null;
    case "facebook":
      return values.facebookUsername.trim()
        ? `https://www.facebook.com/${values.facebookUsername.trim()}`
        : null;
    case "tiktok":
      return values.tiktokUsername.trim()
        ? `https://www.tiktok.com/@${values.tiktokUsername.trim().replace(/^@/, "")}`
        : null;
    case "spotify":
      return values.spotifyId.trim()
        ? `https://open.spotify.com/show/${values.spotifyId.trim()}`
        : null;
  }
}

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

const PLATFORM_SYNC_MIN_AGE_MS = 0;

function platformLastSyncedAt(state?: MediaChannelSyncedFields): Date | undefined {
  if (!state?.lastSyncedAt) return undefined;
  const date = new Date(state.lastSyncedAt);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function isPlatformSyncDue(state?: MediaChannelSyncedFields): boolean {
  const last = platformLastSyncedAt(state);
  return !last || Date.now() - last.getTime() >= PLATFORM_SYNC_MIN_AGE_MS;
}

function formatDay(date: Date | string): string {
  return new Date(date).toLocaleDateString(undefined, { dateStyle: "medium" });
}

const compactNumber = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });

function formatFollowers(platform: PlatformKey, followers: number): string {
  return `${compactNumber.format(followers)} ${platform === "youtube" ? "subscribers" : "followers"}`;
}

function discoverySummary(results: MediaChannelAccountDiscoveryResult[]): {
  severity: "success" | "info" | "warning";
  lines: string[];
} {
  const lines: string[] = [];
  const filled = results.filter((r) => r.status === "filled");
  if (filled.length > 0) {
    lines.push(`Added ${filled.map((r) => `${PLATFORM_LABELS[r.platform]} ${r.value}`).join(", ")}.`);
  }
  for (const r of results) {
    if (r.status === "ambiguous") {
      lines.push(`${PLATFORM_LABELS[r.platform]} lists several accounts (${r.candidates?.join(", ")}); set it manually.`);
    } else if (r.status === "taken") {
      lines.push(`${PLATFORM_LABELS[r.platform]} ${r.value} is already on another channel.`);
    } else if (r.status === "unverified") {
      lines.push(
        `${PLATFORM_LABELS[r.platform]}: YouTube links to "${r.value}", which no longer exists. Google suggestions for the current account are below the ${PLATFORM_LABELS[r.platform]} field.`,
      );
    }
  }
  if (lines.length === 0) lines.push("No new accounts in this channel's YouTube About links.");
  const hasWarnings = results.some((r) => r.status === "ambiguous" || r.status === "taken");
  return { severity: hasWarnings ? "warning" : filled.length > 0 ? "success" : "info", lines };
}

function SectionCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function DetailRow({ label, value, copyable }: { label: string; value?: string | null; copyable?: boolean }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ py: 0.75 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontFamily: copyable ? "ui-monospace, monospace" : undefined, fontSize: copyable ? 12 : undefined }}
          noWrap
          title={value ?? undefined}
        >
          {value || "—"}
        </Typography>
        {copyable && value ? (
          <Tooltip title="Copy">
            <IconButton size="small" onClick={() => void navigator.clipboard.writeText(value)}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </Stack>
  );
}

function syncResultChip(result: MediaChannelImageSyncResult) {
  if (result.status === "updated") {
    return <Chip size="small" variant="outlined" color="success" label="Synced" />;
  }
  if (result.status === "skipped") {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={result.nextSyncAt ? `Skipped · next sync ${formatDay(result.nextSyncAt)}` : "Skipped"}
      />
    );
  }
  return <Chip size="small" variant="outlined" color="warning" label={result.error || "Sync failed"} />;
}

function profileUrl(platform: MediaChannelAccountSearchResult["platform"], value: string): string {
  switch (platform) {
    case "instagram":
      return `https://www.instagram.com/${value}/`;
    case "facebook":
      return `https://www.facebook.com/${value}`;
    case "tiktok":
      return `https://www.tiktok.com/@${value}`;
    case "spotify":
      return `https://open.spotify.com/show/${value}`;
  }
}

function AccountSuggestions({
  result,
  onPick,
  onDismiss,
}: {
  result: MediaChannelAccountSearchResult;
  onPick: (value: string) => void;
  onDismiss: () => void;
}) {
  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {result.suggestions.length > 0
            ? "Google suggestions. Click one to fill the field, then Save."
            : "No accounts found in Google results."}
        </Typography>
        <Button size="small" onClick={onDismiss} sx={{ minWidth: 0, py: 0 }}>
          Dismiss
        </Button>
      </Stack>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {result.suggestions.map((s) => {
          const status = s.takenBy
            ? `Already used by ${s.takenBy.name ?? "another channel"}`
            : s.exists
              ? "Account exists"
              : "Couldn't confirm this account exists";
          return (
            <Tooltip
              key={s.value}
              title={
                <>
                  <div>{s.title ?? s.link}</div>
                  <div>
                    {status} · {s.hits} result{s.hits === 1 ? "" : "s"}
                  </div>
                </>
              }
            >
              <Chip
                size="small"
                variant="outlined"
                color={s.takenBy ? "default" : s.exists ? "success" : "warning"}
                label={`${s.value}${s.hits > 1 ? ` ×${s.hits}` : ""}`}
                disabled={Boolean(s.takenBy)}
                onClick={() => onPick(s.value)}
                deleteIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                onDelete={() => window.open(profileUrl(result.platform, s.value), "_blank", "noopener,noreferrer")}
              />
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
}

function PlatformRow({
  platform,
  state,
  url,
  syncResult,
  onRemove,
  removing,
  onMarkNone,
  markedNone,
  onSearch,
  searching,
  children,
}: {
  platform: PlatformKey;
  state?: MediaChannelSyncedFields;
  url: string | null;
  syncResult?: MediaChannelImageSyncResult;
  /** Set when the saved account can be deleted. */
  onRemove?: () => void;
  removing?: boolean;
  /** Set when the field is empty: records that the channel has no account on this platform. */
  onMarkNone?: () => void;
  /** The channel is recorded as having no account here; discovery and sync leave it empty. */
  markedNone?: boolean;
  /** Set when the field is empty and a Google search can suggest accounts. */
  onSearch?: () => void;
  searching?: boolean;
  children: React.ReactNode;
}) {
  const imageS3Key = state?.imageS3Key;
  const name = state?.name;
  const lastSyncedAt = platformLastSyncedAt(state);
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5 }}>
      <Avatar
        src={imageS3Key ? getS3Url(imageS3Key) : undefined}
        variant="rounded"
        sx={{ width: 48, height: 48, fontSize: 14, bgcolor: "action.selected", color: "text.secondary" }}
      >
        {PLATFORM_LABELS[platform].slice(0, 2)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {PLATFORM_LABELS[platform]}
          </Typography>
          {name ? (
            <Typography variant="body2" color="text.secondary" noWrap title={name} sx={{ minWidth: 0 }}>
              · {name}
            </Typography>
          ) : null}
          {typeof state?.followers === "number" ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flexShrink: 0 }}
              title={state.followers.toLocaleString()}
            >
              · {formatFollowers(platform, state.followers)}
            </Typography>
          ) : null}
          {url ? (
            <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          ) : null}
          {onRemove ? (
            <Tooltip title={`Delete ${PLATFORM_LABELS[platform]} account`}>
              <span>
                <IconButton
                  size="small"
                  disabled={removing}
                  onClick={() => {
                    if (window.confirm(`Delete the ${PLATFORM_LABELS[platform]} account from this channel?`)) {
                      onRemove();
                    }
                  }}
                  aria-label={`Delete ${PLATFORM_LABELS[platform]} account`}
                >
                  {removing ? <CircularProgress size={14} /> : <DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {onMarkNone ? (
            <Tooltip title={`This channel has no ${PLATFORM_LABELS[platform]} account`}>
              <span>
                <IconButton
                  size="small"
                  disabled={removing}
                  onClick={onMarkNone}
                  aria-label={`Mark ${PLATFORM_LABELS[platform]} as no account`}
                >
                  {removing ? <CircularProgress size={14} /> : <BlockIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {markedNone ? (
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              No account
            </Typography>
          ) : null}
          {onSearch ? (
            <Tooltip title={`Search Google for the ${PLATFORM_LABELS[platform]} account (uses 1 SerpApi search)`}>
              <span>
                <IconButton
                  size="small"
                  disabled={searching}
                  onClick={onSearch}
                  aria-label={`Search ${PLATFORM_LABELS[platform]} account`}
                >
                  {searching ? <CircularProgress size={14} /> : <SearchIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {syncResult ? syncResultChip(syncResult) : null}
          <Box sx={{ flex: 1 }} />
          {lastSyncedAt ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flexShrink: 0 }}
              title={`Next sync available ${formatDay(new Date(lastSyncedAt.getTime() + PLATFORM_SYNC_MIN_AGE_MS))}`}
            >
              Last synced {formatDay(lastSyncedAt)}
            </Typography>
          ) : url ? (
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              Never synced
            </Typography>
          ) : null}
        </Stack>
        {children}
      </Box>
    </Stack>
  );
}

function ChannelEditor({
  summary,
  channel,
  syncResults,
  autoSearchPlatforms,
  onSaved,
}: {
  summary: MediaChannelRow;
  channel: MediaChannelDetail;
  syncResults: MediaChannelImageSyncResult[] | null;
  /** New array per "Find accounts" run; each listed empty platform gets a Google search. */
  autoSearchPlatforms: Array<"instagram" | "facebook" | "tiktok" | "spotify"> | null;
  onSaved: () => void;
}) {
  const [initial, setInitial] = useState<ChannelEditValues>(() => editValuesFromRow(summary));
  const [form, setForm] = useState<ChannelEditValues>(initial);
  const [justSaved, setJustSaved] = useState(false);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);

  const serverValues = useMemo(() => editValuesFromRow(summary), [summary]);
  const lastServerValues = useRef(serverValues);
  useEffect(() => {
    const previous = lastServerValues.current;
    if (JSON.stringify(previous) === JSON.stringify(serverValues)) return;
    lastServerValues.current = serverValues;
    // Fields the user hasn't touched follow the server (e.g. a username filled by platform sync).
    setForm((current) => {
      const next = { ...current };
      for (const key of Object.keys(serverValues) as Array<keyof ChannelEditValues>) {
        if (current[key] === previous[key]) (next as Record<string, unknown>)[key] = serverValues[key];
      }
      return next;
    });
    setInitial(serverValues);
  }, [serverValues]);

  const mutation = useMutation({
    mutationFn: () => apiService.patchAdminMediaChannel(summary._id, form),
    onSuccess: (data) => {
      const saved = editValuesFromRow(data.summary);
      setInitial(saved);
      setForm(saved);
      setJustSaved(true);
      onSaved();
    },
  });

  const setField = <K extends keyof ChannelEditValues>(field: K, value: ChannelEditValues[K]) => {
    setJustSaved(false);
    setForm((current) => ({ ...current, [field]: value }));
  };

  const resultFor = (platform: PlatformKey) => syncResults?.find((r) => r.platform === platform);
  const platforms = channel.platforms ?? {};

  type RemovablePlatform = "instagram" | "facebook" | "tiktok" | "spotify";
  const removeMutation = useMutation({
    mutationFn: (platform: RemovablePlatform) => apiService.removeAdminMediaChannelPlatform(summary._id, platform),
    onSuccess: () => onSaved(),
  });
  const removeProps = (platform: RemovablePlatform, savedValue: string | undefined) => {
    const removing = removeMutation.isPending && removeMutation.variables === platform;
    if (savedValue?.trim()) return { onRemove: () => removeMutation.mutate(platform), removing };
    if (channel.removedPlatforms?.includes(platform)) return { markedNone: true };
    if (form[PLATFORM_FIELD[platform]].trim()) return {};
    return { onMarkNone: () => removeMutation.mutate(platform), removing };
  };

  const PLATFORM_FIELD = {
    instagram: "instagramUsername",
    facebook: "facebookUsername",
    tiktok: "tiktokUsername",
    spotify: "spotifyId",
  } as const satisfies Record<RemovablePlatform, keyof ChannelEditValues>;
  const [searchResults, setSearchResults] = useState<
    Partial<Record<RemovablePlatform, MediaChannelAccountSearchResult>>
  >({});
  const [searching, setSearching] = useState<Partial<Record<RemovablePlatform, boolean>>>({});
  const [searchError, setSearchError] = useState<unknown>(null);
  const runSearch = async (platform: RemovablePlatform) => {
    setSearchError(null);
    setSearching((current) => ({ ...current, [platform]: true }));
    try {
      const data = await apiService.searchAdminMediaChannelPlatform(summary._id, platform);
      setSearchResults((current) => ({ ...current, [data.platform]: data }));
    } catch (error) {
      setSearchError(error);
    } finally {
      setSearching((current) => ({ ...current, [platform]: false }));
    }
  };
  // "Find accounts" hands over platforms whose YouTube link is dead; search those once.
  useEffect(() => {
    for (const platform of autoSearchPlatforms ?? []) {
      if (!form[PLATFORM_FIELD[platform]].trim() && !searchResults[platform] && !searching[platform]) {
        void runSearch(platform);
      }
    }
    // Only a new hand-over should trigger searches, not form edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearchPlatforms]);
  const dismissSearch = (platform: RemovablePlatform) =>
    setSearchResults((current) => {
      const next = { ...current };
      delete next[platform];
      return next;
    });
  const searchProps = (platform: RemovablePlatform) =>
    form[PLATFORM_FIELD[platform]].trim()
      ? {}
      : {
          onSearch: () => void runSearch(platform),
          searching: Boolean(searching[platform]),
        };
  const suggestionsFor = (platform: RemovablePlatform) => {
    const result = searchResults[platform];
    return result ? (
      <AccountSuggestions
        result={result}
        onPick={(value) => {
          setField(PLATFORM_FIELD[platform], value);
          dismissSearch(platform);
        }}
        onDismiss={() => dismissSearch(platform)}
      />
    ) : null;
  };

  return (
    <Box
      component="form"
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <Stack spacing={3}>
        <SectionCard title="General">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Name"
                size="small"
                fullWidth
                required
                value={form.name}
                onChange={(event) => setField("name", event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Slug"
                size="small"
                fullWidth
                value={form.slug}
                onChange={(event) => setField("slug", event.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Description"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={(event) => setField("description", event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={form.type}
                  onChange={(event) => setField("type", event.target.value as ChannelEditValues["type"])}
                >
                  <MenuItem value="tv">TV</MenuItem>
                  <MenuItem value="noticias">Noticias</MenuItem>
                  <MenuItem value="radio">Radio</MenuItem>
                  <MenuItem value="digital">Digital</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={form.category}
                  onChange={(event) =>
                    setField("category", event.target.value as ChannelEditValues["category"])
                  }
                >
                  <MenuItem value="politics">Politics</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.trackLivestream}
                    onChange={(event) => setField("trackLivestream", event.target.checked)}
                  />
                }
                label="Track livestream"
              />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard title="Platforms">
          <Stack divider={<Divider flexItem />}>
            <PlatformRow
              platform="youtube"
              state={platforms.youtube}
              url={platformUrl("youtube", form)}
              syncResult={resultFor("youtube")}
            >
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 7 }}>
                  <TextField
                    label="Channel id"
                    size="small"
                    fullWidth
                    placeholder="UC…"
                    value={form.youtubeChannelId}
                    onChange={(event) => setField("youtubeChannelId", event.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField
                    label="Username"
                    size="small"
                    fullWidth
                    placeholder="without @"
                    value={form.youtubeUsername}
                    onChange={(event) => setField("youtubeUsername", event.target.value)}
                  />
                </Grid>
              </Grid>
            </PlatformRow>
            <PlatformRow
              platform="instagram"
              state={platforms.instagram}
              url={platformUrl("instagram", form)}
              syncResult={resultFor("instagram")}
              {...removeProps("instagram", platforms.instagram?.username)}
              {...searchProps("instagram")}
            >
              <TextField
                label="Username"
                size="small"
                fullWidth
                value={form.instagramUsername}
                onChange={(event) => setField("instagramUsername", event.target.value)}
              />
              {suggestionsFor("instagram")}
            </PlatformRow>
            <PlatformRow
              platform="facebook"
              state={platforms.facebook}
              url={platformUrl("facebook", form)}
              syncResult={resultFor("facebook")}
              {...removeProps("facebook", platforms.facebook?.username)}
              {...searchProps("facebook")}
            >
              <TextField
                label="Username or page id"
                size="small"
                fullWidth
                value={form.facebookUsername}
                onChange={(event) => setField("facebookUsername", event.target.value)}
              />
              {suggestionsFor("facebook")}
            </PlatformRow>
            <PlatformRow
              platform="tiktok"
              state={platforms.tiktok}
              url={platformUrl("tiktok", form)}
              syncResult={resultFor("tiktok")}
              {...removeProps("tiktok", platforms.tiktok?.username)}
              {...searchProps("tiktok")}
            >
              <TextField
                label="Username"
                size="small"
                fullWidth
                value={form.tiktokUsername}
                onChange={(event) => setField("tiktokUsername", event.target.value)}
              />
              {suggestionsFor("tiktok")}
            </PlatformRow>
            <PlatformRow
              platform="spotify"
              state={platforms.spotify}
              url={platformUrl("spotify", form)}
              syncResult={resultFor("spotify")}
              {...removeProps("spotify", platforms.spotify?.id)}
              {...searchProps("spotify")}
            >
              <TextField
                label="Show id or URL"
                size="small"
                fullWidth
                value={form.spotifyId}
                onChange={(event) => setField("spotifyId", event.target.value)}
              />
              {suggestionsFor("spotify")}
            </PlatformRow>
          </Stack>
          {searchError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {apiErrorMessage(searchError, "Google search failed.")}
            </Alert>
          ) : null}
          {removeMutation.isError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {apiErrorMessage(removeMutation.error, "Failed to delete the account.")}
            </Alert>
          ) : null}
        </SectionCard>

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            position: dirty || mutation.isPending ? "sticky" : "static",
            bottom: 16,
            zIndex: 1,
            boxShadow: dirty ? 6 : 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {mutation.isError ? (
              <Typography variant="body2" color="error">
                {apiErrorMessage(mutation.error, "Failed to save channel.")}
              </Typography>
            ) : dirty ? (
              <Typography variant="body2" color="warning.main">
                Unsaved changes
              </Typography>
            ) : justSaved ? (
              <Typography variant="body2" color="success.main">
                Channel saved
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Edits update the channels collection only.
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              disabled={!dirty || mutation.isPending}
              onClick={() => {
                mutation.reset();
                setForm(initial);
              }}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!dirty || mutation.isPending || !form.name.trim()}
            >
              {mutation.isPending ? "Saving…" : "Save"}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
}

export function LegacyMediaChannelsRedirect() {
  const [params] = useSearchParams();
  const channelId = params.get("channelId");
  return (
    <Navigate
      replace
      to={channelId ? generatePath(ROUTES_NAMES.CHANNEL_DETAIL, { channelId }) : ROUTES_NAMES.CHANNELS}
    />
  );
}

export const MediaChannelDetailPage: React.FC = () => {
  const { channelId } = useParams<{ channelId: string }>();
  const queryClient = useQueryClient();
  const [syncResults, setSyncResults] = useState<MediaChannelImageSyncResult[] | null>(null);
  const [channelImageResult, setChannelImageResult] = useState<MediaChannelMainImageSyncResult | null>(null);

  const detailQuery = useQuery({
    queryKey: ["admin-media-channel-detail", channelId],
    queryFn: () => apiService.getAdminMediaChannelDetail(channelId!),
    enabled: Boolean(channelId),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin-media-channels"] });
    void queryClient.invalidateQueries({ queryKey: ["admin-media-channel-detail", channelId] });
  };

  const addPodcastMutation = useMutation({
    mutationFn: () => apiService.addAdminMediaChannelPodcast(channelId!),
    onSuccess: (data) => {
      if (data.channelId && data.channelId !== channelId) {
        navigate(generatePath(ROUTES_NAMES.CHANNEL_DETAIL, { channelId: data.channelId }));
        return;
      }
      invalidate();
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => apiService.syncAdminMediaChannelImages(channelId!),
    onSuccess: (data) => {
      setSyncResults(data.results);
      setChannelImageResult(data.channelImage);
      invalidate();
    },
  });

  const [discoveryResults, setDiscoveryResults] = useState<MediaChannelAccountDiscoveryResult[] | null>(null);
  const discoverMutation = useMutation({
    mutationFn: () => apiService.discoverAdminMediaChannelAccounts(channelId!),
    onSuccess: (data) => {
      setDiscoveryResults(data.results);
      invalidate();
    },
  });

  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteMutation = useMutation({
    mutationFn: () => apiService.deleteAdminMediaChannel(channelId!),
    onSuccess: () => {
      setDeleteOpen(false);
      queryClient.removeQueries({ queryKey: ["admin-media-channel-detail", channelId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-media-channels"] });
      navigate(ROUTES_NAMES.CHANNELS);
    },
  });

  const summary = detailQuery.data?.summary;
  const channel = detailQuery.data?.channel;
  const savedPlatforms = channel?.platforms ?? {};
  const identifiedPlatforms = [
    { id: savedPlatforms.youtube?.channelId, state: savedPlatforms.youtube },
    { id: savedPlatforms.instagram?.username, state: savedPlatforms.instagram },
    { id: savedPlatforms.facebook?.username, state: savedPlatforms.facebook },
    { id: savedPlatforms.tiktok?.username, state: savedPlatforms.tiktok },
    { id: savedPlatforms.spotify?.id, state: savedPlatforms.spotify },
  ].filter((entry) => Boolean(entry.id?.trim()));
  const channelImageKey = summary?.imageS3Key ?? undefined;
  const channelImageNeedsCopy = channelImageKey
    ? channelImageKey.startsWith("podcasts/")
    : Boolean(savedPlatforms.youtube?.imageS3Key || savedPlatforms.youtube?.channelId?.trim());
  const hasSyncablePlatform =
    channelImageNeedsCopy || identifiedPlatforms.some((entry) => isPlatformSyncDue(entry.state));
  const nextSyncTimes = identifiedPlatforms
    .map((entry) => platformLastSyncedAt(entry.state)?.getTime())
    .filter((time): time is number => time !== undefined)
    .map((time) => time + PLATFORM_SYNC_MIN_AGE_MS);
  const nextSyncAt = nextSyncTimes.length > 0 ? new Date(Math.min(...nextSyncTimes)) : null;
  const syncDisabledReason =
    hasSyncablePlatform
      ? ""
      : identifiedPlatforms.length === 0
        ? "Save a YouTube channel id or a platform username first"
        : `Platforms sync at most once every 30 days. Next sync available ${
            nextSyncAt ? formatDay(nextSyncAt) : "later"
          }.`;
  const removedPlatforms = channel?.removedPlatforms ?? [];
  const hasMissingAccount = (
    [
      ["instagram", savedPlatforms.instagram?.username],
      ["facebook", savedPlatforms.facebook?.username],
      ["tiktok", savedPlatforms.tiktok?.username],
      ["spotify", savedPlatforms.spotify?.id],
    ] as const
  ).some(([platform, value]) => !value?.trim() && !removedPlatforms.includes(platform));
  const discoverDisabledReason = !savedPlatforms.youtube?.channelId?.trim()
    ? "Save a YouTube channel id first"
    : !hasMissingAccount
      ? "Instagram, TikTok, Facebook and Spotify are already set or deleted"
      : "Fills empty accounts from the links on the channel's YouTube About page";
  const discovery = discoveryResults ? discoverySummary(discoveryResults) : null;
  const autoSearchPlatforms = useMemo(
    () => (discoveryResults ? discoveryResults.filter((r) => r.status === "unverified").map((r) => r.platform) : null),
    [discoveryResults],
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Button
        component={RouterLink}
        to={ROUTES_NAMES.CHANNELS}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 2 }}
      >
        Channels
      </Button>

      {detailQuery.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : detailQuery.error || !summary || !channel ? (
        <Alert severity="error">Failed to load channel.</Alert>
      ) : (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 3 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={getPodcastImageUrl({ imageS3Key: summary.imageS3Key })}
                alt={summary.name}
                sx={{ width: 64, height: 64 }}
              />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {summary.name}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                  <SourceChips source={summary.source} />
                  <Chip size="small" label={summary.type} />
                  <Chip size="small" label={summary.category} />
                  {summary.trackLivestream ? (
                    <Chip size="small" color="success" variant="outlined" label="Tracking livestream" />
                  ) : null}
                  {channelImageResult?.status === "updated" ? (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={
                        channelImageResult.source === "youtube"
                          ? "Channel image set from YouTube"
                          : "Channel image copied from podcast"
                      }
                    />
                  ) : channelImageResult?.status === "error" ? (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`Channel image: ${channelImageResult.error ?? "failed"}`}
                    />
                  ) : null}
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Tooltip title={discoverDisabledReason}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={discoverMutation.isPending ? <CircularProgress size={16} /> : <PersonSearchIcon />}
                    onClick={() => {
                      setDiscoveryResults(null);
                      discoverMutation.mutate();
                    }}
                    disabled={
                      discoverMutation.isPending || !savedPlatforms.youtube?.channelId?.trim() || !hasMissingAccount
                    }
                  >
                    {discoverMutation.isPending ? "Finding accounts…" : "Find accounts"}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={syncDisabledReason}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={syncMutation.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
                    onClick={() => {
                      setSyncResults(null);
                      setChannelImageResult(null);
                      syncMutation.mutate();
                    }}
                    disabled={syncMutation.isPending || !hasSyncablePlatform}
                  >
                    {syncMutation.isPending ? "Syncing images…" : "Sync platform images"}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  deleteMutation.reset();
                  setDeleteOpen(true);
                }}
              >
                Delete
              </Button>
            </Stack>
          </Stack>

          <Dialog
            open={deleteOpen}
            onClose={() => {
              if (!deleteMutation.isPending) setDeleteOpen(false);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Delete channel</DialogTitle>
            <DialogContent>
              {deleteMutation.isError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {apiErrorMessage(deleteMutation.error, "Failed to delete channel.")}
                </Alert>
              ) : null}
              <Typography gutterBottom>
                Delete <strong>{summary.name}</strong> from the channels collection? This cannot be undone.
              </Typography>
              {summary.legacyPodcastId || summary.legacyLivestreamChannelId ? (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  This channel is copied from a {summary.legacyPodcastId ? "podcast" : "livestream channel"} that
                  stays in place. Channel sync can re-create it the next time that source is saved.
                </Alert>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteOpen(false)} disabled={deleteMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </Button>
            </DialogActions>
          </Dialog>

          {syncMutation.isError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiErrorMessage(syncMutation.error, "Failed to sync platform images.")}
            </Alert>
          ) : null}
          {discoverMutation.isError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiErrorMessage(discoverMutation.error, "Failed to find platform accounts.")}
            </Alert>
          ) : null}
          {discovery ? (
            <Alert severity={discovery.severity} sx={{ mb: 2 }} onClose={() => setDiscoveryResults(null)}>
              {discovery.lines.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </Alert>
          ) : null}

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <ChannelEditor
                key={summary._id}
                summary={summary}
                channel={channel}
                syncResults={syncResults}
                autoSearchPlatforms={autoSearchPlatforms}
                onSaved={invalidate}
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              <Stack spacing={3}>
                <SectionCard
                  title="Details"
                  action={
                    summary.legacyPodcastId ? undefined : (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={addPodcastMutation.isPending}
                        onClick={() => addPodcastMutation.mutate()}
                      >
                        {addPodcastMutation.isPending ? "Adding…" : "Add podcast"}
                      </Button>
                    )
                  }
                >
                  <Stack divider={<Divider flexItem />}>
                    {addPodcastMutation.isError ? (
                      <Alert severity="error" sx={{ my: 1 }}>
                        {apiErrorMessage(addPodcastMutation.error, "Failed to add podcast.")}
                      </Alert>
                    ) : null}
                    <DetailRow label="Channel id" value={summary._id} copyable />
                    <DetailRow label="Legacy podcast" value={summary.legacyPodcastId} copyable />
                    <DetailRow label="Legacy livestream" value={summary.legacyLivestreamChannelId} copyable />
                    <DetailRow label="Created" value={formatDate(channel.createdAt)} />
                    <DetailRow label="Updated" value={formatDate(channel.updatedAt)} />
                  </Stack>
                </SectionCard>

                <Accordion variant="outlined" disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Raw JSON
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      component="pre"
                      sx={(theme) => ({
                        p: 1.5,
                        m: 0,
                        borderRadius: 1,
                        bgcolor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50],
                        fontFamily: "ui-monospace, monospace",
                        fontSize: 12,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: 420,
                        overflow: "auto",
                      })}
                    >
                      {JSON.stringify(channel, null, 2)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </Grid>

            <Grid size={12}>
              <ChannelProgramsSection channelId={summary._id} channelType={summary.type} />
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};
